import { create } from "zustand";
import {
  createSandbox,
  deleteSandbox,
  fetchScopeGroups,
  listApiKeys,
  listSandboxes,
} from "./api";
import type { ScopeGroup } from "./scopes";
import type { CreateSandboxInput, Sandbox, SandboxApiKey } from "./types";

// Not per-account and not served by any API; pf-broker carries the same literal.
export const SANDBOX_BASE_URL = "https://sandbox.atlas.propertyfinder.com";

const POLL_MS = 6000;
const POLL_LIMIT = 20;

export type LoadStatus = "idle" | "loading" | "ready" | "error";

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([entry]) => entry !== key),
  );
}

function isProvisioning(sandbox: Sandbox): boolean {
  return sandbox.status === "creating" || sandbox.status === "pending";
}

interface SandboxesState {
  sandboxes: Sandbox[] | null;
  maxActive: number | null;
  status: LoadStatus;
  refreshing: boolean;
  keys: Record<string, SandboxApiKey[]>;
  keyStatus: Record<string, LoadStatus>;
  scopeGroups: ScopeGroup[] | null;
  scopeStatus: LoadStatus;
  pollsLeft: number;
  load: () => Promise<void>;
  loadScopeGroups: () => Promise<void>;
  refresh: () => Promise<void>;
  loadKeys: (accountId: string) => Promise<void>;
  create: (input: CreateSandboxInput) => Promise<void>;
  remove: (accountId: string) => Promise<void>;
}

let pollTimer: ReturnType<typeof setTimeout> | null = null;

export const useSandboxesStore = create<SandboxesState>((set, get) => {
  function schedulePoll() {
    if (pollTimer) clearTimeout(pollTimer);
    const { sandboxes, pollsLeft } = get();
    if (!sandboxes?.some(isProvisioning) || pollsLeft <= 0) return;
    pollTimer = setTimeout(() => {
      set({ pollsLeft: get().pollsLeft - 1 });
      void get().refresh();
    }, POLL_MS);
  }

  function prefetch(sandboxes: Sandbox[]) {
    void get().loadScopeGroups();
    for (const sandbox of sandboxes) {
      if (isProvisioning(sandbox) || sandbox.status === "failed") continue;
      void get().loadKeys(sandbox.id);
    }
  }

  async function read(refreshing: boolean) {
    set(refreshing ? { refreshing: true } : { status: "loading" });
    try {
      const { sandboxes, maxActive } = await listSandboxes();
      set({ sandboxes, maxActive, status: "ready", refreshing: false });
      prefetch(sandboxes);
      schedulePoll();
    } catch {
      set({ status: "error", refreshing: false });
    }
  }

  return {
    sandboxes: null,
    maxActive: null,
    status: "idle",
    refreshing: false,
    keys: {},
    keyStatus: {},
    scopeGroups: null,
    scopeStatus: "idle",
    pollsLeft: POLL_LIMIT,

    load: async () => {
      if (get().status === "loading") return;
      await read(false);
    },

    refresh: async () => {
      if (get().refreshing) return;
      await read(true);
    },

    loadScopeGroups: async () => {
      const status = get().scopeStatus;
      if (status === "loading" || status === "ready") return;
      set({ scopeStatus: "loading" });
      try {
        set({ scopeGroups: await fetchScopeGroups(), scopeStatus: "ready" });
      } catch {
        set({ scopeStatus: "error" });
      }
    },

    loadKeys: async (accountId) => {
      const status = get().keyStatus[accountId];
      if (status === "loading" || status === "ready") return;
      set((s) => ({ keyStatus: { ...s.keyStatus, [accountId]: "loading" } }));
      try {
        const keys = await listApiKeys(accountId);
        set((s) => ({
          keys: { ...s.keys, [accountId]: keys },
          keyStatus: { ...s.keyStatus, [accountId]: "ready" },
        }));
      } catch {
        set((s) => ({ keyStatus: { ...s.keyStatus, [accountId]: "error" } }));
      }
    },

    create: async (input) => {
      const sandbox = await createSandbox(input);
      set((s) => ({
        sandboxes: [...(s.sandboxes ?? []), sandbox],
        pollsLeft: POLL_LIMIT,
      }));
      schedulePoll();
    },

    remove: async (accountId) => {
      await deleteSandbox(accountId);
      set((s) => ({
        sandboxes: (s.sandboxes ?? []).filter((item) => item.id !== accountId),
        keys: without(s.keys, accountId),
        keyStatus: without(s.keyStatus, accountId),
      }));
    },
  };
});

export function useQuota(): {
  total: number;
  active: number;
  maxActive: number | null;
  atQuota: boolean;
  canCreate: boolean;
} {
  const sandboxes = useSandboxesStore((s) => s.sandboxes) ?? [];
  const maxActive = useSandboxesStore((s) => s.maxActive);
  const active = sandboxes.filter(
    (item) => item.status === "active" || isProvisioning(item),
  ).length;
  return {
    total: sandboxes.length,
    active,
    maxActive,
    atQuota: maxActive !== null && active >= maxActive,
    // An unknown cap is not permission to create.
    canCreate: maxActive !== null && active < maxActive,
  };
}
